import { loadErrorBannerSx } from '@/features/shared/components/chartPanels/chartLoadErrorBannerSx';
import { TABLE_GRID_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { getErrorCopy } from '@/shared/copy/errors';
import { s } from '@/theme/tokens';
import { ChartEmptyState, CHART_EMPTY_STATE_FILTER_HINT, StatusBanner } from '@/ui/composites';
import { useDeferredAsyncViewState } from '@/ui/composites/useDeferredAsyncViewState';
import { Stack } from '@/ui/layout/Stack';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import {
  DAILY_STATUS_BLOCK_SIZE,
  DAILY_STATUS_ROW_HEIGHT,
  HEADER_SEPARATOR_SX,
  HEADER_SUBTEXT_SX,
  HEADER_TEXT_SX,
  HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX,
  HISTORY_PANEL_CONTENT_HEADER_HEIGHT,
  HISTORY_STATE_GRACE_PERIOD_MS,
  ROW_NAME_TEXT_SX,
  formatHistoryHeaderDay,
  resolveHistoryBodyMinHeight,
} from './EntityDailyHistoryPanel.constants';
import { EntityDailyHistoryRows } from './EntityDailyHistoryPanelRows';
import { EntityDailyHistoryPanelSkeleton } from './EntityDailyHistoryPanelSkeleton';
import type { DailyHistoryCell, EntityDailyHistoryRow } from './EntityDailyHistoryPanel.types';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';

type EntityDailyHistoryPanelBodyProps = {
  title: string;
  entityLabel: string;
  entityHeaderContent: ReactNode;
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs';
  rows: EntityDailyHistoryRow[];
  loading: boolean;
  knownEmpty?: boolean;
  showErrorState: boolean;
  noRowsText: string;
  days: DailyHistoryCell[];
  visibleDayHeaderIndices: number[];
  dayHeaderGridRef: RefObject<HTMLDivElement | null>;
  nameColumnHeaderCellRef: RefObject<HTMLTableCellElement | null>;
  panelTableContainerRef: RefObject<HTMLDivElement | null>;
  nameColumnWidth: string;
  pageSize: number;
  paginatedRows: EntityDailyHistoryRow[];
  expandedRowIds: Set<string>;
  linksDisabled: boolean;
  onToggleRowExpansion: (rowId: string) => void;
  onNameColumnResizeStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onNameColumnResizeKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
};

export const EntityDailyHistoryPanelBody = ({
  title,
  entityLabel,
  entityHeaderContent,
  activityLabel,
  rows,
  loading,
  knownEmpty = false,
  showErrorState,
  noRowsText,
  days,
  visibleDayHeaderIndices,
  dayHeaderGridRef,
  nameColumnHeaderCellRef,
  panelTableContainerRef,
  nameColumnWidth,
  pageSize,
  paginatedRows,
  expandedRowIds,
  linksDisabled,
  onToggleRowExpansion,
  onNameColumnResizeStart,
  onNameColumnResizeKeyDown,
}: EntityDailyHistoryPanelBodyProps): ReactElement => {
  const emptyStateMinHeight = resolveHistoryBodyMinHeight({
    rowCount: pageSize,
    rowHeight: DAILY_STATUS_ROW_HEIGHT,
    headerHeight: HISTORY_PANEL_CONTENT_HEADER_HEIGHT,
  });
  const hasEmptyState = rows.length === 0 || days.length === 0;
  const effectiveLoading = knownEmpty && hasEmptyState && !showErrorState ? false : loading;
  const viewState = useDeferredAsyncViewState({
    loading: effectiveLoading,
    hasError: showErrorState,
    isEmpty: hasEmptyState,
    delayMs: HISTORY_STATE_GRACE_PERIOD_MS,
  });
  const visibleDayHeaderLabels = visibleDayHeaderIndices.map((index) => ({
    day: days[index] as DailyHistoryCell,
    index,
  }));

  if (viewState.showPlaceholder || viewState.showSkeleton) {
    return (
      <Box
        ref={panelTableContainerRef}
        sx={{ overflowX: 'auto', backgroundColor: 'background.paper' }}
      >
        <Box sx={{ overflow: 'hidden' }}>
          <EntityDailyHistoryPanelSkeleton
            dayCount={days.length > 0 ? days.length : 7}
            days={days}
            entityLabel={entityLabel}
            rowCount={pageSize}
            nameColumnWidth={nameColumnWidth}
            rowHeight={DAILY_STATUS_ROW_HEIGHT}
            blockSize={DAILY_STATUS_BLOCK_SIZE}
            headerHeight={HISTORY_PANEL_CONTENT_HEADER_HEIGHT}
            visibleDayHeaderIndices={visibleDayHeaderIndices}
          />
        </Box>
      </Box>
    );
  }

  if (viewState.showError) {
    return (
      <Box
        ref={panelTableContainerRef}
        sx={{ overflowX: 'auto', backgroundColor: 'background.paper' }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            px: s('md'),
            pb: s('sm'),
            minHeight: emptyStateMinHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <StatusBanner
            tone="red"
            variant="outlined"
            message={getErrorCopy('banner.historyLoadFailed')}
            sx={loadErrorBannerSx}
          />
        </Box>
      </Box>
    );
  }

  if (viewState.showEmpty) {
    return (
      <Box
        ref={panelTableContainerRef}
        sx={{ overflowX: 'auto', backgroundColor: 'background.paper' }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            px: s('md'),
            pb: s('sm'),
            minHeight: emptyStateMinHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChartEmptyState title={noRowsText} body={CHART_EMPTY_STATE_FILTER_HINT} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={panelTableContainerRef}
      sx={{ overflowX: 'auto', backgroundColor: 'background.paper' }}
    >
      <Table
        aria-label={title}
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          backgroundColor: 'background.paper',
          '& .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
            borderTop: 'none',
            pt: s('sm'),
          },
          '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
            pb: s('sm'),
          },
          '& .MuiTableCell-root, & .MuiTableRow-root, & .MuiTableHead-root, & .MuiTableBody-root': {
            backgroundColor: 'background.paper',
          },
        }}
      >
        <colgroup>
          <col width={nameColumnWidth} />
          <col width="100%" />
        </colgroup>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: 'background.paper',
              height: `${TABLE_GRID_HEADER_HEIGHT + 14}px`,
              position: 'relative',
              '&::after': buildTableDividerPseudoSx('bottom'),
            }}
          >
            <TableCell
              ref={nameColumnHeaderCellRef}
              sx={[
                {
                  backgroundColor: 'background.paper',
                  width: nameColumnWidth,
                  minWidth: nameColumnWidth,
                  maxWidth: nameColumnWidth,
                  px: s('md'),
                  pt: s('md'),
                  pb: s('none'),
                  verticalAlign: 'top',
                  borderBottom: 'none',
                  position: 'relative',
                },
                HEADER_SEPARATOR_SX,
              ]}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap="sm"
                sx={{ minWidth: 0, width: '100%' }}
              >
                <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                  {entityLabel}
                </Text>
                {entityHeaderContent}
              </Stack>
              <Box
                component="button"
                type="button"
                aria-label={`Resize ${entityLabel} column`}
                onPointerDown={onNameColumnResizeStart}
                onKeyDown={onNameColumnResizeKeyDown}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX / -2)}px`,
                  width: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX)}px`,
                  height: '100%',
                  p: s('none'),
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'col-resize',
                  zIndex: 1,
                  touchAction: 'none',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: s('sm'),
                    bottom: s('sm'),
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    borderRadius: '999px',
                    borderLeft: '2px solid',
                    borderColor: 'divider',
                    opacity: 0,
                    transition: (muiTheme) =>
                      muiTheme.transitions.create('opacity', {
                        duration: muiTheme.transitions.duration.shortest,
                      }),
                  },
                  '&:hover::after, &:focus-visible::after, &:active::after': {
                    opacity: 1,
                  },
                }}
              />
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: 'background.paper',
                width: '100%',
                px: s('md'),
                pt: s('md'),
                pb: s('none'),
                verticalAlign: 'top',
                borderBottom: 'none',
              }}
            >
              <Stack gap="sm" sx={{ minWidth: 0 }}>
                <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                  Status
                </Text>
                <Box
                  ref={dayHeaderGridRef}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${String(days.length)}, minmax(0, 1fr))`,
                    columnGap: s('md'),
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                    height: '16px',
                    pb: s('none'),
                  }}
                >
                  {days.map((day, index) => {
                    const visibleLabel = visibleDayHeaderLabels.find(
                      (entry) => entry.index === index,
                    );

                    return (
                      <Box
                        key={`${day.day.toISOString()}-${String(index)}`}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          minWidth: 0,
                          height: '16px',
                        }}
                      >
                        {visibleLabel ? (
                          <Text
                            role="body"
                            sx={[
                              HEADER_SUBTEXT_SX,
                              {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                              },
                            ]}
                          >
                            {formatHistoryHeaderDay(visibleLabel.day.day)}
                          </Text>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <EntityDailyHistoryRows
            rows={paginatedRows}
            expandedRowIds={expandedRowIds}
            onToggleRowExpansion={onToggleRowExpansion}
            rowHeight={DAILY_STATUS_ROW_HEIGHT}
            blockSize={DAILY_STATUS_BLOCK_SIZE}
            nameColumnWidth={nameColumnWidth}
            rowNameTextSx={ROW_NAME_TEXT_SX}
            activityLabel={activityLabel}
            linksDisabled={linksDisabled}
          />
        </TableBody>
      </Table>
    </Box>
  );
};
